import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { streamerService } from '../services/api';
import type { StreamerPublicDto } from '../types';

type StreamersContextType = {
    streamers: StreamerPublicDto[];
    onlineCount: number;
    loading: boolean;
    refresh: () => Promise<void>;
};

const POLL_INTERVAL_MS = 60000;

const StreamersContext = createContext<StreamersContextType>({
    streamers: [],
    onlineCount: 0,
    loading: true,
    refresh: async () => {},
});

export function StreamersProvider({ children }: { children: React.ReactNode }) {
    const [streamers, setStreamers] = useState<StreamerPublicDto[]>([]);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const data = await streamerService.getStreamers();
            setStreamers(
                Array.isArray(data)
                    ? data.filter((s) => Array.isArray(s.channels) && s.channels.length > 0)
                    : []
            );
        } catch {
            setStreamers([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
        const timer = setInterval(refresh, POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [refresh]);

    const isLive = (s: StreamerPublicDto) => s.channels.some((c) => c.isLive);

    const value: StreamersContextType = {
        streamers,
        onlineCount: streamers.filter(isLive).length,
        loading,
        refresh,
    };

    return (
        <StreamersContext.Provider value={value}>
            {children}
        </StreamersContext.Provider>
    );
}

export function useStreamers(): StreamersContextType {
    return useContext(StreamersContext);
}